import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Chip, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TextField, InputAdornment, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Alert, LinearProgress, Tooltip,
  ToggleButtonGroup, ToggleButton, Button, Stack, Divider,
} from '@mui/material';
import {
  Search as SearchIcon, Warning as WarningIcon, CheckCircle as CheckIcon,
  Schedule as ScheduleIcon, Error as ErrorIcon, CalendarMonth as CalendarIcon,
  TableRows as TableRowsIcon, Download as DownloadIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { differenceInDays, format } from 'date-fns';
import api from '../services/api';
import { setProjectPhase } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PageSearchBar from '../components/PageSearchBar';

const PHASE_NAMES = ['Proposal', 'Engineering & Kick-off', 'Rough-in', 'Pre-Final', 'Final', 'Final Billing', 'AfterCare'];
const PHASE_COLORS = ['#78909c', '#1565c0', '#e65100', '#7b1fa2', '#2e7d32', '#c62828', '#00695c'];
const HEALTH_COLORS = { green: '#2e7d32', yellow: '#f57c00', red: '#c62828' };

const fmtDate = (d) => d ? format(new Date(d), 'MMM d, yyyy') : '—';
const fmtCurrency = (v) => {
  const n = parseFloat(v) || 0;
  if (n >= 1e6) return `$${(n/1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n/1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const SummaryCard = ({ label, value, color = '#0D2C54', sub, icon, onClick }) => (
  <Paper elevation={2} onClick={onClick} sx={{ p: 2, borderLeft: `4px solid ${color}`, cursor: onClick ? 'pointer' : 'default', transition: 'all 0.2s', '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: 4 } : {} }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      {icon}
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
    <Typography variant="h4" fontWeight="bold" color={color}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

export default function ProjectTimeline() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pmFilter, setPmFilter] = useState(user?.role === 'PM' ? String(user?.id || '') : '');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [healthFilter, setHealthFilter] = useState('');
  const [issueFilter, setIssueFilter] = useState('all');
  const [salesFilter, setSalesFilter] = useState('');
  const [sort, setSort] = useState({ col: 'days_in_phase', dir: 'desc' });
  // PM engagement scorecard + legacy candidate map (Tom Clancy 2026-07-13)
  const [engagement, setEngagement] = useState(null);
  const [legacyMap, setLegacyMap] = useState({}); // id -> 'strong' | 'moderate'
  const [legacySummary, setLegacySummary] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/projects', { params: { approved: 'true' } })
      .then(r => setProjects(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // PM engagement scorecard (per-PM: dates set since 7/7)
  useEffect(() => {
    api.get('/projects/pm-engagement')
      .then(r => setEngagement(r.data))
      .catch(() => setEngagement(null));
  }, []);

  // Legacy candidates — pull the moderate list once; chip color decides
  // strong vs moderate. Tom Clancy 2026-07-13.
  useEffect(() => {
    api.get('/projects/legacy-candidates', { params: { tier: 'moderate' } })
      .then(r => {
        const m = {};
        for (const row of r.data.rows || []) m[row.id] = row.legacy_tier;
        setLegacyMap(m);
        setLegacySummary(r.data.summary);
      })
      .catch(() => { setLegacyMap({}); setLegacySummary(null); });
  }, []);

  const handleDownloadSuggestClosure = useCallback(async (tier = 'strong') => {
    try {
      const resp = await api.get(`/projects/export/suggest-closure`, {
        params: { tier }, responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ACS_Suggest_Closure_${tier}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to download Suggest Closure report: ' + (e.message || 'unknown'));
    }
  }, []);

  const handleSort = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === 'desc' ? 'asc' : 'desc' }));

  const handlePhaseChange = async (projectId, newPhase, projectName) => {
    try {
      await setProjectPhase(projectId, newPhase);
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, current_phase: newPhase } : p));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to change phase');
    }
  };

  // Enrich projects with computed fields
  //
  // Tom Clancy 2026-07-13: "Missing timeline" = the PM hasn't entered
  // installation_start_date AND projected_completion_date in Command
  // Center. Sage's est_start / est_comp count as *reference* signals but
  // don't satisfy the PM's obligation — the whole point of the 7/7
  // email was to have PMs own the dates inside CC.
  const enriched = useMemo(() => projects.map(p => {
    const startDate = p.installation_start_date || p.sage_est_start || p.started_at || p.created_at;
    const daysInPhase = p.started_at ? differenceInDays(new Date(), new Date(p.started_at)) : 0;
    const hasEndDate = !!p.projected_completion_date;
    const hasStartDate = !!p.installation_start_date;
    const endDate = p.projected_completion_date || p.sage_est_comp;
    const daysToEnd = endDate ? differenceInDays(new Date(endDate), new Date()) : null;
    const overdue = daysToEnd !== null && daysToEnd < 0;
    // Missing timeline = missing EITHER PM-owned date (start OR end).
    const noTimeline = !(hasStartDate && hasEndDate);
    const stale = daysInPhase > 90 && p.current_phase < 6;
    const legacyTier = legacyMap[p.id] || null; // 'strong' | 'moderate' | null
    return { ...p, startDate, daysInPhase, hasEndDate, hasStartDate,
             endDate, daysToEnd, overdue, noTimeline, stale, legacyTier };
  }), [projects, legacyMap]);

  // PM list
  const pmList = useMemo(() => {
    const pms = [...new Map(projects.filter(p => p.pm_name).map(p => [p.pm_id, { id: p.pm_id, name: p.pm_name }])).values()];
    return pms.sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  // Sales list — Tom Clancy 2026-07-13 asked for filterability by sales rep.
  // Build from the users-table join (sales_id + sales_name) — not the free-text
  // D-Tools sales_rep_name — otherwise reps whose names appear as stale D-Tools
  // strings on projects owned by someone else create label/id collisions in the
  // dropdown. Marc Flamm 2026-07-20.
  const salesList = useMemo(() => {
    const reps = [...new Map(
      projects.filter(p => p.sales_id && p.sales_name)
              .map(p => [p.sales_id, { id: p.sales_id, name: p.sales_name }])
    ).values()];
    return reps.sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  // Filter
  const filtered = useMemo(() => {
    let list = enriched;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(p => (p.name || '').toLowerCase().includes(s) || (p.client_name || '').toLowerCase().includes(s) || (p.primary_job_number || '').includes(s));
    }
    if (pmFilter) list = list.filter(p => String(p.pm_id) === pmFilter);
    if (salesFilter) list = list.filter(p => String(p.sales_id) === salesFilter);
    if (phaseFilter) list = list.filter(p => String(p.current_phase) === phaseFilter);
    if (healthFilter) list = list.filter(p => p.health_status === healthFilter);
    if (issueFilter === 'no_dates') list = list.filter(p => p.noTimeline);
    else if (issueFilter === 'overdue') list = list.filter(p => p.overdue);
    else if (issueFilter === 'stale') list = list.filter(p => p.stale);
    else if (issueFilter === 'red') list = list.filter(p => p.health_status === 'red');
    else if (issueFilter === 'legacy') list = list.filter(p => p.legacyTier);
    return list;
  }, [enriched, search, pmFilter, salesFilter, phaseFilter, healthFilter, issueFilter]);

  // Sort
  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    let av = a[sort.col], bv = b[sort.col];
    if (['days_in_phase', 'daysToEnd', 'current_phase', 'total_proposal_value'].includes(sort.col)) {
      av = parseFloat(av) || 0; bv = parseFloat(bv) || 0;
    } else if (['startDate', 'endDate', 'created_at'].includes(sort.col)) {
      av = av ? new Date(av).getTime() : 0; bv = bv ? new Date(bv).getTime() : 0;
    } else {
      av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase();
    }
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  }), [filtered, sort]);

  // Stats
  const stats = useMemo(() => ({
    total: enriched.length,
    noTimeline: enriched.filter(p => p.noTimeline).length,
    overdue: enriched.filter(p => p.overdue).length,
    stale: enriched.filter(p => p.stale).length,
    red: enriched.filter(p => p.health_status === 'red').length,
    legacy: enriched.filter(p => p.legacyTier).length,
  }), [enriched]);

  const sl = (col, label) => (
    <TableSortLabel active={sort.col === col} direction={sort.col === col ? sort.dir : 'asc'} onClick={() => handleSort(col)}
      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#ccc !important' } }}>{label}</TableSortLabel>
  );

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>Project Timeline</Typography>
        <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>{sorted.length} of {stats.total} projects · What needs attention</Typography>
      </Box>

      {/* Issue cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={2}>
          <SummaryCard label="Active Projects" value={stats.total} icon={<CheckIcon sx={{ color: '#2e7d32' }} />} color="#2e7d32" onClick={() => setIssueFilter('all')} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <SummaryCard label="No Timeline Dates" value={stats.noTimeline} icon={<WarningIcon sx={{ color: '#e65100' }} />} color="#e65100" sub="PMs need to set dates" onClick={() => setIssueFilter('no_dates')} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <SummaryCard label="Overdue" value={stats.overdue} icon={<ErrorIcon sx={{ color: '#c62828' }} />} color="#c62828" sub="Past completion date" onClick={() => setIssueFilter('overdue')} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <SummaryCard label="Stale (90+ days)" value={stats.stale} icon={<ScheduleIcon sx={{ color: '#7b1fa2' }} />} color="#7b1fa2" sub="In same phase 90+ days" onClick={() => setIssueFilter('stale')} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <SummaryCard label="Red Status" value={stats.red} icon={<ErrorIcon sx={{ color: '#c62828' }} />} color="#c62828" onClick={() => setIssueFilter('red')} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <SummaryCard
            label="Legacy Candidates"
            value={stats.legacy}
            icon={<ArchiveIcon sx={{ color: '#455a64' }} />}
            color="#455a64"
            sub="Labor & billing ≥ 90%"
            onClick={() => setIssueFilter('legacy')}
          />
        </Grid>
      </Grid>

      {/* PM Engagement scorecard (Tom Clancy 2026-07-13) —
          who has actually set project dates since the 7/7 WIP email. */}
      {engagement && engagement.pm_scorecard && engagement.pm_scorecard.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#fafbfc' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
                 alignItems={{ xs: 'stretch', md: 'center' }}
                 justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0D2C54' }}>
                PM Engagement — dates set since the 7/7 WIP email
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {engagement.totals.both_dates_set} of {engagement.totals.wip_jobs} active WIP jobs have both dates set ·
                {' '}${Number(engagement.totals.missing_dates_value || 0).toLocaleString()} contract $ still missing timeline
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Download the Suggested Closure report (Strong tier: labor & billing ≥ 95%). Review and email to Pat + Jeanne.">
                <span>
                  <Button variant="contained" size="small" startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadSuggestClosure('strong')}
                    sx={{ textTransform: 'none' }}>
                    Suggest Closure (Strong)
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Download the wider list (Moderate tier: labor & billing ≥ 90%). More candidates, less certainty.">
                <span>
                  <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadSuggestClosure('moderate')}
                    sx={{ textTransform: 'none' }}>
                    Moderate Tier
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#eef1f5', '& th': { fontWeight: 700, fontSize: '0.72rem', color: '#0D2C54' } }}>
                  <TableCell>Project Manager</TableCell>
                  <TableCell align="right">WIP Jobs</TableCell>
                  <TableCell align="right">Missing Dates</TableCell>
                  <TableCell align="right">Both Dates Set</TableCell>
                  <TableCell align="right">Coverage</TableCell>
                  <TableCell align="right">WIP $</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Last Activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {engagement.pm_scorecard.map(row => {
                  const wip = Number(row.wip_jobs) || 0;
                  const set = Number(row.both_dates_set) || 0;
                  const coverage = wip > 0 ? set / wip : 0;
                  const covColor = coverage >= 0.9 ? '#2e7d32' : coverage >= 0.5 ? '#f57c00' : '#c62828';
                  return (
                    <TableRow key={row.pm_id} hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => { setPmFilter(String(row.pm_id)); setIssueFilter('no_dates'); }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{row.pm_name}</Typography>
                      </TableCell>
                      <TableCell align="right">{wip}</TableCell>
                      <TableCell align="right">
                        <Chip label={row.missing_dates} size="small"
                          sx={{ bgcolor: Number(row.missing_dates) > 0 ? '#fdecea' : '#e8f5e9',
                                color:   Number(row.missing_dates) > 0 ? '#c62828' : '#2e7d32',
                                fontWeight: 700, height: 20, fontSize: '0.72rem' }} />
                      </TableCell>
                      <TableCell align="right">{set}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} sx={{ color: covColor }}>
                          {Math.round(coverage * 100)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">${Number(row.wip_value || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color={row.last_login ? 'text.primary' : 'text.disabled'}>
                          {row.last_login ? format(new Date(row.last_login), 'MMM d, h:mma') : 'never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color={row.last_touch ? 'text.primary' : 'text.disabled'}>
                          {row.last_touch ? format(new Date(row.last_touch), 'MMM d, h:mma') : 'none'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Click a row to jump to that PM's projects still missing a timeline. Cutoff: {new Date(engagement.cutoff).toLocaleDateString()}.
          </Typography>
        </Paper>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <PageSearchBar placeholder="Search project, client, job#…" value={search} onChange={(v) => setSearch(v)} />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Project Manager</InputLabel>
          <Select value={pmFilter} label="Project Manager" onChange={e => setPmFilter(e.target.value)}>
            <MenuItem value="">All PMs</MenuItem>
            {pmList.map(pm => <MenuItem key={pm.id} value={pm.id}>{pm.name}</MenuItem>)}
          </Select>
        </FormControl>
        {/* Sales filter — Tom Clancy 2026-07-13 */}
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Sales Rep</InputLabel>
          <Select value={salesFilter} label="Sales Rep" onChange={e => setSalesFilter(e.target.value)}>
            <MenuItem value="">All Sales</MenuItem>
            {salesList.map(sp => <MenuItem key={sp.id} value={sp.id}>{sp.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Phase</InputLabel>
          <Select value={phaseFilter} label="Phase" onChange={e => setPhaseFilter(e.target.value)}>
            <MenuItem value="">All Phases</MenuItem>
            {PHASE_NAMES.map((n, i) => <MenuItem key={i+1} value={String(i+1)}>{i+1}. {n}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Health</InputLabel>
          <Select value={healthFilter} label="Health" onChange={e => setHealthFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="green">Green</MenuItem>
            <MenuItem value="yellow">Yellow</MenuItem>
            <MenuItem value="red">Red</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={2} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow sx={{ bgcolor: '#0D2C54', '& th': { color: '#fff', fontWeight: 700, fontSize: '0.75rem' } }}>
            <TableCell>{sl('primary_job_number', 'Job #')}</TableCell>
            <TableCell>{sl('name', 'Project')}</TableCell>
            <TableCell>{sl('pm_name', 'PM')}</TableCell>
            <TableCell>{sl('sales_rep_name', 'Sales')}</TableCell>
            <TableCell>{sl('current_phase', 'Phase')}</TableCell>
            <TableCell>{sl('health_status', 'Health')}</TableCell>
            <TableCell align="center">{sl('days_in_phase', 'Days in Phase')}</TableCell>
            <TableCell>{sl('startDate', 'Start Date')}</TableCell>
            <TableCell>{sl('endDate', 'Target Completion')}</TableCell>
            <TableCell align="center">{sl('daysToEnd', 'Days Left')}</TableCell>
            <TableCell align="right">{sl('total_proposal_value', 'Value')}</TableCell>
            <TableCell>Flags</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {sorted.map((p, i) => (
              <TableRow key={p.id} hover onClick={() => navigate(`/projects/${p.id}`)}
                sx={{ cursor: 'pointer', bgcolor: p.overdue ? 'rgba(198,40,40,0.06)' : p.stale ? 'rgba(123,31,162,0.04)' : i % 2 ? 'action.hover' : undefined }}>
                <TableCell>
                  <Typography variant="body2" color="primary.main" fontWeight="bold">{p.primary_job_number || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 250 }}>{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>{p.client_name}</Typography>
                </TableCell>
                <TableCell><Typography variant="body2" noWrap>{p.pm_name || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2" noWrap>{p.sales_name || p.sales_rep_name || '—'}</Typography></TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  <Select size="small" value={p.current_phase}
                    onChange={e => { e.stopPropagation(); handlePhaseChange(p.id, e.target.value, p.name); }}
                    onClick={e => e.stopPropagation()}
                    sx={{ fontSize: '0.75rem', fontWeight: 600, height: 28, minWidth: 120, '.MuiSelect-select': { py: 0.3 } }}>
                    {PHASE_NAMES.map((n, i) => <MenuItem key={i+1} value={i+1} sx={{ fontSize: '0.75rem' }}>{i+1}. {n}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell>
                  <Chip label={p.health_status} size="small"
                    sx={{ textTransform: 'capitalize', bgcolor: HEALTH_COLORS[p.health_status] || '#999', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight="bold" color={p.stale ? 'error.main' : p.daysInPhase > 60 ? 'warning.main' : 'text.primary'}>
                    {p.daysInPhase || '—'}
                  </Typography>
                </TableCell>
                <TableCell><Typography variant="caption">{fmtDate(p.startDate)}</Typography></TableCell>
                <TableCell>
                  {p.hasEndDate ? (
                    <Typography variant="caption">{fmtDate(p.endDate)}</Typography>
                  ) : (
                    <Chip label="No date set" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                  )}
                </TableCell>
                <TableCell align="center">
                  {p.daysToEnd !== null ? (
                    <Typography variant="body2" fontWeight="bold"
                      color={p.daysToEnd < 0 ? 'error.main' : p.daysToEnd < 14 ? 'warning.main' : 'success.main'}>
                      {p.daysToEnd < 0 ? `${Math.abs(p.daysToEnd)}d late` : `${p.daysToEnd}d`}
                    </Typography>
                  ) : '—'}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={p.total_proposal_value > 0 ? 'bold' : 'normal'}
                    color={p.total_proposal_value > 0 ? 'success.main' : 'text.secondary'}>
                    {p.total_proposal_value > 0 ? fmtCurrency(p.total_proposal_value) : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {p.legacyTier === 'strong' ? (
                    <Tooltip title="Labor ≥ 95% and Billed ≥ 95%. Very likely done — candidate for closure.">
                      <Chip label="Legacy" size="small" icon={<ArchiveIcon sx={{ fontSize: 14 }} />}
                        sx={{ bgcolor: '#455a64', color: '#fff', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                    </Tooltip>
                  ) : p.legacyTier === 'moderate' ? (
                    <Tooltip title="Labor ≥ 90% and Billed ≥ 90%. Possible legacy — worth a review.">
                      <Chip label="Legacy?" size="small" variant="outlined"
                        sx={{ borderColor: '#78909c', color: '#455a64', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow><TableCell colSpan={12} sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No projects match filters</Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
